import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Users, Mic, Calculator, User as UserIcon } from "lucide-react";
import { haptic } from "@/hooks/useIsMobile";
import { useLanguage } from "@/contexts/LanguageContext";
import { zapnuto } from "@/lib/funkce";
const tabLabel = (s, lang) => (lang === "sk" && s === "Domů" ? "Domov" : s);

/* MobileLayout — luxury bank-style shell for mobile PWA.
 * Renders the active route inside a styled container, shows bottom tab nav
 * with an elevated floating REC button in the middle.
 *
 * Design tokens aligned with /app/design_guidelines.json:
 *  - Background: #060A14 (OLED-friendly)
 *  - Surface: #10204A/40 + backdrop-blur
 *  - Accent: #F47920 with glow
 *  - Haptic feedback on every tab switch
 *  - Safe-area aware (iOS notch + Android gesture bar)
 */

const TABS = [
  { to: "/m", icon: Home, label: "Domů", exact: true },
  { to: "/m/crm", icon: Users, label: "CRM" },
  { to: "/m/voice", icon: Mic, label: "REC", center: true },
  { to: "/m/calc", icon: Calculator, label: "Kalk." },
  { to: "/m/profile", icon: UserIcon, label: "Profil" },
];

function TabIcon({ Icon, active, elevated }) {
  if (elevated) {
    return (
      <motion.div
        whileTap={{ scale: 0.9 }}
        className="flex items-center justify-center w-14 h-14 rounded-full"
        style={{
          background: "linear-gradient(135deg, #F47920 0%, #FF9853 100%)",
          boxShadow: "0 8px 32px rgba(244,121,32,0.5), 0 0 0 4px rgba(6,10,20,1)",
        }}
      >
        <Icon size={22} strokeWidth={2.2} className="text-white" />
      </motion.div>
    );
  }
  return (
    <Icon
      size={22}
      strokeWidth={1.8}
      className={active ? "text-[#F47920]" : "text-white/50"}
    />
  );
}

export default function MobileLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const handleTab = (to) => {
    haptic(30);
    navigate(to);
  };

  return (
    // .mob-dark: re-override v index.css vrací .text-white na bílou — globální
    // navy-killer pravidlo by jinak udělalo tmavý text na téměř černém pozadí.
    <div
      className="min-h-screen text-white mob-dark"
      style={{
        background: "#060A14",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Top safe area filler */}
      <div
        aria-hidden
        className="fixed top-0 left-0 right-0 z-40 pointer-events-none"
        style={{
          height: "env(safe-area-inset-top)",
          background: "#060A14",
        }}
      />

      {/* Page content */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: [0.22, 0.8, 0.36, 1] }}
          className="pt-[calc(env(safe-area-inset-top)+8px)] pb-[calc(88px+env(safe-area-inset-bottom))]"
        >
          <Outlet />
        </motion.main>
      </AnimatePresence>

      {/* Bottom Tab Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50"
        style={{
          paddingBottom: "env(safe-area-inset-bottom)",
          background: "rgba(6,10,20,0.85)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}
        data-testid="mobile-bottom-nav"
      >
        <div className="h-20 px-4 flex items-center justify-between relative">
          {TABS.filter((tab) => tab.to !== "/m/voice" || zapnuto("aiTlacitka")).map((tab) => { /* REC = placená AI (lib/funkce.js) */
            const isActive =
              tab.exact
                ? location.pathname === tab.to
                : location.pathname.startsWith(tab.to);
            if (tab.center) {
              return (
                <button
                  key={tab.to}
                  onClick={() => handleTab(tab.to)}
                  data-testid={`mobile-tab-${tab.label.toLowerCase()}`}
                  className="flex flex-col items-center justify-center absolute left-1/2 -translate-x-1/2 -top-5 gap-1"
                  aria-label={tabLabel(tab.label, lang)}
                >
                  <TabIcon Icon={tab.icon} elevated />
                  <span className="text-[10px] font-medium text-white/60 mt-0.5">
                    {tabLabel(tab.label, lang)}
                  </span>
                </button>
              );
            }
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.exact}
                onClick={() => haptic(30)}
                data-testid={`mobile-tab-${tab.label.toLowerCase()}`}
                className="flex-1 flex flex-col items-center justify-center gap-1"
              >
                <TabIcon Icon={tab.icon} active={isActive} />
                <span
                  className={`text-[10px] font-medium transition-colors ${
                    isActive ? "text-[#F47920]" : "text-white/50"
                  }`}
                >
                  {tabLabel(tab.label, lang)}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* AI asistent schovaný i tady (velký test funkčnosti 24. 10. 2026) — na desktopu
          je skrytý od dřívějška (App.js), AI je na pauze kvůli kreditům Emergentu.
          Vrátit = odkomentovat řádek a vrátit import AIAssistantWidget. */}
      {/* <AIAssistantWidget /> */}
    </div>
  );
}
